// src/components/principal/WebsiteApiKey.jsx (v6 – config-driven, clean)

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";

/**
 * Panel for principals to view and share their WordPress plugin API key.
 *
 * Auto-generates key if missing, provides prefilled email copy for web admin,
 * and handles non-WordPress cases.
 *
 * @component
 * @param {Object} props
 * @param {string} props.websiteTechUsed - From profile (WordPress or other)
 * @param {string} props.schoolId - School ID for email
 * @param {string} props.principalName - For email signature
 * @param {function} props.onError - Error reporting callback
 * @param {function} props.onApiKeyReady - Callback to notify parent tab (for indicator)
 * @returns {JSX.Element}
 */
const WebsiteApiKey = ({
  websiteTechUsed,
  schoolId,
  principalName,
  onError,
  onApiKeyReady,
}) => {
  const [apiKey, setApiKey] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const copyText = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setStatus("Copied to clipboard!");
        setTimeout(() => setStatus(""), 3000);
      })
      .catch(() => setStatus("Failed to copy."));
  };

  const copyPrefilledEmail = () => {
    const body = `Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (search for "GhrianGhraffy").

Once installed and activated:

1. Go to Settings → General in WordPress.
2. Scroll to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click "Connect to GhrianGhraffy".
5. Confirm the correct school name and School ID appear.
6. Save changes at the bottom.

API Key: ${apiKey}

(If needed, our School ID is: ${schoolId})

This will create a secure "Photos" page on the website.

Please let me know when it's done.

Kind regards,
${principalName || "School Principal"}`;

    copyText(body);
  };

  useEffect(() => {
    // Non-WordPress site
    if (websiteTechUsed === "Your School Website is not built with WordPress") {
      setApiKey(null);
      setStatus("");
      onApiKeyReady?.(false);
      return;
    }

    const fetchOrGenerateKey = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(API_PATHS.GET_API_KEY);
        setApiKey(data.api_key);
        setStatus("");
        onApiKeyReady?.(true);
      } catch (e) {
        if (e.response?.status === 404) {
          // No key yet — generate one
          try {
            const { data } = await api.post(API_PATHS.GENERATE_API_KEY, {});
            setApiKey(data.api_key);
            setStatus("API key created successfully!");
            onApiKeyReady?.(true);
          } catch (gerr) {
            console.error("Generate API key failed:", gerr);
            setStatus("Failed to generate API key. Please try again.");
            onApiKeyReady?.(false);
            onError?.("Failed to generate API key.");
          }
        } else {
          console.error("Fetch API key failed:", e);
          setStatus("Failed to load API key. Please refresh.");
          onApiKeyReady?.(false);
          onError?.("Failed to load API key.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrGenerateKey();
  }, [websiteTechUsed, onApiKeyReady, onError]);

  // Non-WordPress message
  if (websiteTechUsed === "Your School Website is not built with WordPress") {
    return (
      <div className="principal-panel website-panel">
        <h3 className="panel-title">Your School’s Website</h3>
        <p className="no-wordpress-message">
          Your website is not built with WordPress.{" "}
          <a href="/contact" className="template-download-link">
            Contact us
          </a>{" "}
          for integration options.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="principal-panel website-panel">
        <p>Loading API key…</p>
      </div>
    );
  }

  if (!apiKey) {
    onApiKeyReady?.(false);
    return (
      <div className="principal-panel website-panel">
        <h3 className="panel-title">Plugin API Access</h3>
        <p>Unable to load or generate API key. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="principal-panel website-panel">
      <div className="plugin-status-row">
        <div className="plugin-status-copy">
          <h3 className="panel-title">Your School’s Website</h3>
          <p className="api-key-helper-text">
            Your API key is ready for the GhrianGhraffy WordPress plugin.
          </p>
        </div>
        <span className="status-pill status-pill--ready">
          <span className="status-pill-dot" aria-hidden="true" />
          API key ready
        </span>
      </div>

      <p>
        Send the following email to your website administrator:
      </p>

      <textarea
        className="api-email-textarea"
        value={`Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (search for "GhrianGhraffy").

Once installed and activated:

1. Go to Settings → General in WordPress.
2. Scroll to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click "Connect to GhrianGhraffy".
5. Confirm the correct school name and School ID appear.
6. Save changes at the bottom.

API Key: ${apiKey}

(If needed, our School ID is: ${schoolId})

This will create a secure "Photos" page on the website.

Please let me know when it's done.

Kind regards,
${principalName || "School Principal"}`}
        readOnly
        onClick={(e) => e.target.select()}
        rows={20}
      />

      <button
        type="button"
        onClick={copyPrefilledEmail}
        className="btn btn-primary website-copy-button"
      >
        Copy Email to Clipboard
      </button>

      {status && <p className="helper-status-message" aria-live="polite">{status}</p>}
    </div>
  );
};

export default WebsiteApiKey;